import React, { useEffect, useState } from 'react';
import { Sparkles } from 'lucide-react';

const STAGES = [
  { at: 0,     label: 'JixxeoAI is thinking' },
  { at: 5000,  label: 'JixxeoAI is working on your answer' },
  { at: 15000, label: 'JixxeoAI is still working' },
  { at: 30000, label: 'JixxeoAI is taking a little longer on this one — thanks for your patience' },
];

/**
 * Animated "JixxeoAI is thinking…" indicator shown while the backend is still
 * waiting to return the first token of the AI response. Automatically escalates
 * its label every few seconds so users never wonder if the UI is frozen.
 *
 * Pass `label` to override (e.g. "JixxeoAI is analyzing your video…" after a
 * video attachment).
 */
export default function ThinkingIndicator({ label, startedAt }) {
  const [stageLabel, setStageLabel] = useState(label || STAGES[0].label);

  useEffect(() => {
    if (label) { setStageLabel(label); return; }
    const t0 = startedAt || Date.now();
    const tick = () => {
      const elapsed = Date.now() - t0;
      let active = STAGES[0];
      for (const s of STAGES) if (elapsed >= s.at) active = s;
      setStageLabel(active.label);
    };
    tick();
    const iv = setInterval(tick, 1000);
    return () => clearInterval(iv);
  }, [label, startedAt]);

  return (
    <div
      data-testid="thinking-indicator"
      className="flex items-start gap-2.5 py-3 animate-in fade-in duration-200"
      aria-live="polite"
      aria-atomic="true"
    >
      <div className="w-8 h-8 rounded-full gradient-brand flex items-center justify-center flex-shrink-0 shadow-md shadow-violet-500/20">
        <Sparkles className="w-4 h-4 text-white" />
      </div>
      <div className="flex items-center gap-2 glass rounded-2xl rounded-tl-sm px-3.5 py-2.5 border border-violet-500/20">
        <span className="text-sm text-slate-200">{stageLabel}</span>
        <span className="inline-flex gap-1 ml-0.5" aria-hidden="true">
          <span className="w-1.5 h-1.5 rounded-full bg-violet-300 jx-dot" style={{ animationDelay: '0ms' }} />
          <span className="w-1.5 h-1.5 rounded-full bg-violet-300 jx-dot" style={{ animationDelay: '200ms' }} />
          <span className="w-1.5 h-1.5 rounded-full bg-violet-300 jx-dot" style={{ animationDelay: '400ms' }} />
        </span>
      </div>
      <style>{`
        @keyframes jx-dot-bounce { 0%, 60%, 100% { transform: translateY(0); opacity: 0.4 } 30% { transform: translateY(-4px); opacity: 1 } }
        .jx-dot { animation: jx-dot-bounce 1.1s ease-in-out infinite; }
      `}</style>
    </div>
  );
}
